import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutInfo } from "@/mocks/about";

export default function AboutPage() {
  const [formState, setFormState] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [formMessage, setFormMessage] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const siteUrl = "https://hanhanmanhua.com";

  useSEO({
    title: "关于我们 - 汗汗漫画",
    description:
      "了解汗汗漫画的品牌故事与发展历程。更新最快的漫画网，提供好看免费漫画大全，无遮挡韩漫日漫国漫在线阅读。联系我们获取更多信息。",
    keywords:
      "汗汗漫画,关于我们,漫画网介绍,品牌故事,联系方式,免费漫画平台",
    canonicalUrl: `${siteUrl}/about`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于汗汗漫画",
        description:
          "了解汗汗漫画的品牌故事与发展历程。更新最快的漫画网。",
        url: `${siteUrl}/about`,
        isPartOf: {
          "@type": "WebSite",
          name: "汗汗漫画",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: aboutInfo.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector(
      'input[name="phone_alt"]'
    ) as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormState("success");
      setFormMessage("感谢您的留言，我们会尽快回复！");
      form.reset();
      return;
    }

    setFormState("submitting");
    setFormMessage("");

    try {
      const formData = new FormData(form);
      const res = await fetch(
        "https://readdy.ai/api/form/d9otj9aqs38sj6jc7fm0",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(
            Array.from(formData.entries()).filter(
              (entry): entry is [string, string] =>
                typeof entry[1] === "string"
            )
          ).toString(),
        }
      );

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      const code = parsed?.code as string | undefined;
      const serverMsg =
        (parsed?.meta as Record<string, string>)?.message ||
        (parsed?.message as string) ||
        responseText ||
        "";

      if (res.ok && code === "OK") {
        setFormState("success");
        setFormMessage("感谢您的留言，我们会尽快回复！");
        form.reset();
      } else {
        setFormState("error");
        setFormMessage(
          serverMsg.includes("spam") || serverMsg.includes("form data is spam")
            ? "提交失败，请稍后再试。"
            : serverMsg || "提交失败，请稍后重试。"
        );
      }
    } catch {
      setFormState("error");
      setFormMessage("网络错误，请稍后重试。");
    }
  };

  return (
    <main>
      <div className="pt-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "关于我们" },
          ]}
        />

        {/* Hero */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于{" "}
              <strong className="text-primary-500">
                {aboutInfo.brandName}
              </strong>
            </h1>
            <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.slogan}
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-8 py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  {aboutInfo.intro}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {aboutInfo.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="p-5 rounded-lg bg-background-50 border border-background-200/30 text-center"
                  >
                    <p className="text-2xl md:text-3xl font-bold text-primary-500">
                      {stat.value}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-8 py-10 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              为什么选择汗汗漫画
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.features.map((feature) => (
                <div
                  key={feature.title}
                  className="p-6 rounded-lg bg-background-100 border border-background-200/30 text-center hover:border-primary-200/50 transition-colors"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i
                      className={`${feature.icon} text-primary-500 text-xl`}
                    ></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-8 py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((item) => (
                <div
                  key={item.name}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/30"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < item.rating
                            ? "ri-star-fill text-amber-500"
                            : "ri-star-line text-foreground-300"
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{item.content}"
                  </p>
                  <p className="text-xs font-semibold text-foreground-800">
                    {item.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-8 py-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {aboutInfo.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-100 border border-background-200/30 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-8 py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto max-w-2xl">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-semibold text-foreground-900 mb-4">
                  联系方式
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <span className="text-sm text-foreground-600">
                      {aboutInfo.contact.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <span className="text-sm text-foreground-600">
                      {aboutInfo.contact.wechat}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-secondary-600"></i>
                    </div>
                    <span className="text-sm text-foreground-600">
                      {aboutInfo.contact.weibo}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-900 mb-4">
                  留言给我们
                </h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-3"
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="您的称呼"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                  <textarea
                    name="message"
                    placeholder="请输入您的留言..."
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      opacity: 0,
                      height: 0,
                      width: 0,
                    }}
                  />
                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="w-full px-6 py-2.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formState === "submitting" ? "提交中..." : "发送留言"}
                  </button>
                  {formMessage && (
                    <p
                      className={`text-xs ${
                        formState === "success"
                          ? "text-accent-600"
                          : "text-red-500"
                      }`}
                    >
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}